import {
  CalendarIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IResource,
  IResourceFetchParams,
  MN_RESOURCES,
  useListContext,
} from '@jobcost-monorepo/crud';
import {
  IListTimesheetFetchParams,
  ITimesheet,
} from '@jobcost-monorepo/crud/dto/timesheet';
import DropdownCrud from '@jobcost-monorepo/crud/ui/DropdownCrud';
import {
  getMonthsKeyValues,
  getYearsKeyValues,
} from '@jobcost-monorepo/crud/utils/helpers/helperKeyValues';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Button, Dropdown, IconSvg, useAuth } from '@jobcost-monorepo/ui';
import { KeyValue, cn, useBreakpoints } from '@jobcost-monorepo/utils';
import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import FiltersTimesheet from './Filters';
import { TimesheetSingleSchema } from './validationSchema';
import dayjs from '@jobcost-monorepo/dayjs-config';

export interface ISearch {
  month: number;
  year: number;
  id?: number;
  jobId?: number;
  orderId?: number;
  resourceId?: number;
  startDate?: string;
  endDate?: string;
}

interface IProps {
  isLocked: boolean;
  handleSearch: (
    resourceId: number,
    startDate: string,
    endDate: string,
    jobId?: number,
    orderId?: number,
  ) => void;
  handleNew: () => void;
  handlePrint: () => void;
  handleReplicate: () => void;
  handleCheckLock: () => void;
}

const TimesheetSingleHeader = ({
  isLocked,
  handleSearch,
  handleNew,
  handlePrint,
  handleReplicate,
  handleCheckLock,
}: IProps): ReactElement => {
  const { isSmall } = useBreakpoints();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const resourceId = user?.resourceId;
  const { t } = useTranslation(['timesheets', 'commons']);
  const months: KeyValue[] = getMonthsKeyValues();
  const years: KeyValue[] = getYearsKeyValues();
  const { params, clearDataList, setParams, repository } = useListContext<
    ITimesheet,
    IListTimesheetFetchParams
  >();

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const defaultValues: ISearch = {
    month: dayjs.utc(params?.startDate).month(),
    year: dayjs.utc(params?.startDate).year(),
    id: isAdmin ? params?.resourceId : resourceId ?? undefined,
    jobId: params?.jobId ?? undefined,
    orderId: params?.orderId ?? undefined,
    resourceId: params?.resourceId,
    startDate: params?.startDate,
    endDate: params?.endDate,
  };

  const isFiltersActive = repository.gridFiltersOptions.some((item) => {
    return params?.[item.linkedField];
  });

  const onSubmit: SubmitHandler<ISearch> = (data) => {
    const startDate = dayjs
      .utc(`${data.year}-${data.month + 1}-01`)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs
      .utc(`${data.year}-${data.month + 1}-01`)
      .endOf('month')
      .format('YYYY-MM-DD');
    const resourceId = data.id;
    const { jobId, orderId } = data;
    methods.setValue('resourceId', resourceId);
    methods.setValue('startDate', startDate);
    methods.setValue('endDate', endDate);

    resourceId && handleSearch(resourceId, startDate, endDate, jobId, orderId);
  };

  const methods = useForm<ISearch>({
    mode: 'onSubmit',
    values: defaultValues,
    defaultValues: JSON.parse(JSON.stringify(defaultValues)),
    resolver: zodResolver(TimesheetSingleSchema),
  });

  const { handleSubmit, getValues } = methods;

  const handleLock = () => {
    if (isAdmin) return handleCheckLock();
    !isLocked && handleCheckLock();
  };

  const getLabelLockUnlock = () => {
    return isLocked
      ? t('grid.unlock', { ns: 'timesheets' })
      : t('grid.lock', { ns: 'timesheets' });
  };

  const onClearResource = () => {
    methods.setValue('id', undefined);
    setParams({ ...params, resourceId: undefined });
    clearDataList && clearDataList();
  };

  useEffect(() => {
    const values = getValues();
    if (!isAdmin && values.id) {
      onSubmit(values);
    }
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col">
        <div
          className={cn(
            'h-max flex w-full flex-wrap items-center justify-between mb-4',
            isSmall ? 'flex-col items-start' : 'flex-row items-center',
          )}
        >
          <div className={cn('flex flew-row items-center flex-wrap')}>
            <div
              className={cn(isSmall && 'w-full', 'flex flex-row items-center')}
            >
              <Dropdown
                name="year"
                errorName="year"
                label={t('filter.year')}
                items={years}
                searchable
                width={cn(!isSmall ? 'w-24' : 'w-1/3')}
              />
              <div className="w-2" />
              <Dropdown
                name="month"
                errorName="month"
                label={t('filter.month')}
                items={months}
                searchable
                width={cn(!isSmall ? 'w-32' : 'w-2/3')}
              />
              {!isAdmin && (
                <>
                  <div className="w-2" />
                  <Button type="submit">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    <span className={cn(isSmall ? 'mr-0' : 'mr-2')} />
                    {!isSmall ? t('filter.update') : ''}
                  </Button>
                </>
              )}
            </div>

            {isAdmin && (
              <div className="flex flex-row items-center w-full ml-0 sm:w-auto sm:ml-2">
                <DropdownCrud<IResource, IResourceFetchParams>
                  moduleName={MN_RESOURCES}
                  linkedField="id"
                  linkedFieldForDefaultValue="firstName"
                  label={t('form.resourceId')}
                  width={cn(!isSmall ? 'w-56' : 'w-full')}
                  forceDisabled={!isAdmin}
                  onClearCallback={onClearResource}
                />
                <div className="w-2" />
                <Button type="submit">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  <div className="hidden sm:ml-1 sm:inline">
                    {t('filter.update')}
                  </div>
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-row items-center w-full space-x-2 sm:w-auto">
            {!isLocked && (
              <>
                <Button
                  type="button"
                  className={cn(isSmall && isAdmin ? 'w-full' : 'w-auto')}
                  onClick={handleReplicate}
                >
                  <CalendarIcon className="h-5 w-5" />
                  <div className="hidden sm:ml-1 sm:inline">
                    {t('replicate.button')}
                  </div>
                </Button>
                <Button
                  type="button"
                  onClick={handleNew}
                  className={cn(isSmall && isAdmin ? 'w-full' : 'w-auto')}
                >
                  <PlusIcon className="h-5 w-5" />
                  <div className="hidden sm:ml-1 sm:inline">
                    {t('grid.new')}
                  </div>
                </Button>
              </>
            )}

            {isAdmin && (
              <Button
                type="button"
                className={cn(isSmall && 'w-full')}
                onClick={handlePrint}
                disabled={!(resourceId || params?.resourceId)}
              >
                <PrinterIcon className="h-5 w-5" />
                <div className="hidden sm:ml-1 sm:inline">
                  {t('grid.print')}
                </div>
              </Button>
            )}

            <Button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={cn(isSmall && isAdmin ? 'w-full' : 'w-auto')}
              disabled={!(resourceId || params?.resourceId)}
            >
              <IconSvg
                name={isFiltersActive ? 'funnelIcon' : 'filterVertical'}
              />
              <div className="hidden sm:ml-1 sm:inline">
                {t('filters.title')}
              </div>
            </Button>

            {(isAdmin || !isLocked) && (
              <Button
                type="button"
                className={cn(
                  isSmall && isAdmin ? 'w-full' : 'w-auto',
                  'whitespace-nowrap',
                )}
                onClick={handleLock}
                disabled={!(resourceId || params?.resourceId)}
              >
                {!isLocked ? (
                  <LockClosedIcon className="h-5 w-5" />
                ) : (
                  <LockOpenIcon className="h-5 w-5" />
                )}
                {(!isAdmin || !isSmall) && <span className="mr-2" />}
                {(!isAdmin || !isSmall) && getLabelLockUnlock()}
              </Button>
            )}
          </div>
        </div>

        {!isAdmin && isLocked && (
          <div className="w-full flex items-center flex-row mb-2">
            <LockClosedIcon className="h-5 w-5 mr-1" />
            {t('grid.locked_month')}
          </div>
        )}

        <FiltersTimesheet
          isOpen={filtersOpen}
          handleClose={() => setFiltersOpen(false)}
          onSubmit={onSubmit}
        />
      </form>
    </FormProvider>
  );
};

export default TimesheetSingleHeader;
